import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
interface InactivityDetectorProps {
  timeout: number; // timeout duration in milliseconds (e.g., 10 * 60 * 1000 for 10 minutes)
  children: React.ReactNode;
  onTimeout?: () => void; // Optional callback function to trigger on inactivity timeout
}

const InactivityDetector: React.FC<InactivityDetectorProps> = ({ timeout, children, onTimeout }) => {
  const dispatch = useDispatch();
  const timerRef = useRef<number | null>(null);
  
  const [isActive, setIsActive] = useState(true);

  const history = useNavigate();
  // Function to reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set timeout for user inactivity
    timerRef.current = window.setTimeout(() => {
      if (onTimeout) {
        onTimeout();
      } 
      handleInactivityAcrossTabs(); // Default logout behavior
      
    }, timeout);

    // Update the last activity time in localStorage
    localStorage.setItem('lastActivityTime', Date.now().toString());
  }, [timeout, onTimeout]);


  // Function to handle user logout
  const handleLogout = useCallback(() => {
    const sessionId=localStorage.getItem('sessionId')
    if(sessionId){
      toast.error("Logging out user due to inactivity.", {
        position: "top-center"
      });
    }
    history("/logout");
  }, [dispatch, history]);

  // Function to handle inactivity across all tabs
  const handleInactivityAcrossTabs = () => {
    const currentTime = Date.now();
    const lastActivityTime = localStorage.getItem('lastActivityTime');
    const lastActivity = lastActivityTime ? parseInt(lastActivityTime, 10) : 0;

    // Check if all tabs are inactive (if no recent activity in the last `timeout` milliseconds)
    if (currentTime - lastActivity > timeout) {
      handleLogout(); // Logout if all tabs are inactive
    } else {
      setIsActive(true); // User is active in at least one tab
    }
  };


  // Event listeners for user activity
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];
    const resetOnActivity = () => resetTimer();

    // Attach listeners to reset the timer on activity
    events.forEach(event => window.addEventListener(event, resetOnActivity));

    // Handle cross-tab inactivity detection
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'lastActivityTime') {
        handleInactivityAcrossTabs();
      }
    };

    // Listen for changes in localStorage (activity updates across tabs)
    window.addEventListener('storage', handleStorageChange);

    // Initialize the timer on mount
    resetTimer();

    // Cleanup event listeners on unmount
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(event => window.removeEventListener(event, resetOnActivity));
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [resetTimer]);
  return <>{children}</>;
};

export default InactivityDetector;